import { useMemo, useState } from 'react';
import type { WarehouseInfo } from '@econtractor/types';
import { WarehouseTable, OffTransfer, type OffTransferShow } from 'modules/warehouse';
import { useAuth } from 'context/AuthContext';
import { useSettings } from 'context/SettingsContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { isObjectEditLocked } from 'utils/helpers/objects/isObjectEditLocked';
import TeamProposalShortage, { type TeamShortageRow } from '../TeamProposalShortage';

interface ObjectCardProductsProps {
    vobjectFull: {
        object?: { objectStatus?: number };
        objectWarehouseId?: number;
        stocks?: unknown;
        proposalShortage?: {
            name?: string;
            products?: TeamShortageRow[];
        } | null;
    } | null;
    fetchObject: () => Promise<void> | void;
}

const ObjectCardProducts = ({ vobjectFull, fetchObject }: ObjectCardProductsProps) => {
    const { auth } = useAuth();
    const { localSettings } = useSettings();
    const params = useSettingsParams();
    const [showTransfer, setShowTransfer] = useState<OffTransferShow>(false);
    const [presetCount, setPresetCount] = useState<number | null>(null);
    const teamId = Number(auth?.user?.access?.team || 0);
    const teamWarehouseId = useMemo(() => {
        const warehouses = (localSettings?.WAREHOUSES || []) as WarehouseInfo[];
        const teamWh = warehouses.find(
            (wh) =>
                Number(wh.teamId) === teamId &&
                !Number(wh.objectId) &&
                !Number(wh.reservObjectId),
        );
        return Number(teamWh?.ID || 0);
    }, [localSettings?.WAREHOUSES, teamId]);
    const objectWarehouseId = Number(vobjectFull?.objectWarehouseId || 0);
    const locked = isObjectEditLocked(vobjectFull?.object?.objectStatus, params);
    const shortage = vobjectFull?.proposalShortage;
    const items = shortage?.products || [];
    const canAdd = !locked && teamWarehouseId > 0 && objectWarehouseId > 0;

    return (
        <div className="px-3">
            <TeamProposalShortage
                kind="products"
                proposalName={String(shortage?.name || '')}
                items={items}
                canAdd={canAdd}
                onAdd={(item) => {
                    const productId = Number(item.productId || 0);
                    if (!(productId > 0)) return;
                    const deficit = Number(item.deficit || 0);
                    setPresetCount(deficit > 0 ? deficit : null);
                    setShowTransfer(productId);
                }}
            />
            {canAdd && (
                <OffTransfer
                    wId={teamWarehouseId}
                    teamId={null}
                    reservObjectId={null}
                    fixedToWarehouseId={objectWarehouseId}
                    fetchStocks={fetchObject}
                    show={showTransfer}
                    setShow={setShowTransfer}
                    presetCount={presetCount}
                />
            )}
            <WarehouseTable
                onlyView={true}
                setShowTransfer={null}
                stocksList={vobjectFull?.stocks}
            />
        </div>
    );
};

export default ObjectCardProducts;
